import { useEffect } from "react";
import type { DependencyList, ReactElement } from "react";

import { useMobileTopBarOverride } from "@/react/components/Mobile/hooks/topBar/useMobileTopBarOverride";

/**
 * Shared registration boilerplate for the `shell/topBar/` registrars:
 * replaces the TopBar with `element` on mount and restores it to `null`
 * on unmount. Mirrors
 * `hooks/sectionTabs/useRegisterMobileSectionTabs`, applied to the
 * topBar/ family.
 *
 * Pass `null` as `element` when the registrar isn't ready to render yet
 * (e.g. required data hasn't loaded) - this clears the override rather
 * than skipping registration, which is equivalent to skipping since the
 * previous run's cleanup already clears it, but keeps every registrar's
 * effect body unconditional.
 *
 * `deps` is forwarded to the underlying effect as-is - include everything
 * `element` was built from, same as any other `useEffect`
 * dependency array.
 */
export const useRegisterTopBarOverride = (
  element: ReactElement | null,
  deps: DependencyList,
) => {
  const { setTopBarOverride } = useMobileTopBarOverride();

  useEffect(() => {
    setTopBarOverride(element);

    return () => {
      // Only clear if this effect's own element is still the active one.
      setTopBarOverride(current => (current === element ? null : current));
    };
  }, [...deps, setTopBarOverride]);
};
